import { useCallback, useMemo, useState } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Box, Stack } from "~/common/mui";
import type { OrderData } from "~/domains/orders/types/modelsV2";
import type { DocumentListRow } from "~/features/orders/orderList/useOrderListState";
import { OrderCard } from "./OrderCard";
import { useOrderListData } from "../orderList/components/OrderListTable/useOrderListData";
import { ORDER_DATA_SORT_HANDLERS, OrderDataSort } from "../orderList/components/OrderListTable/orderDataSorting";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { getOrderLineItemTotalPrice } from "~/features/orders/common/utils/OrderLineItemUtils";
import { ATCBar } from "~/features/orders/common/components/ATCBar";
import OrderDetailsAddToCartFlow from "~/features/orders/orderDetails/components/AddToCart/OrderDetailsAddToCartFlow";
import { useBooleanState } from "~/common/hooks/useBooleanState";

interface Props {
  rows: DocumentListRow<OrderData>[];
  toggleOpen: (row: DocumentListRow<OrderData>) => void;
  toggleOpenAll: () => void;
}

interface SelectionState {
  [soNumber: string]: number[];
}

interface SelectedATCItemsSummary {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

const selectedATCItemsDefaultValue: SelectedATCItemsSummary = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

export const OrderListTableV2 = ({ rows, toggleOpen }: Props) => {
  const { sortedRows } = useOrderListData({
    rows,
    sortHandlers: ORDER_DATA_SORT_HANDLERS,
    initialSortCode: OrderDataSort.DatePlaced,
    translationPrefix: "orderListPage.table.header.",
  });
  const [selectionState, setSelectionState] = useState<SelectionState>({});
  const { val: showAddToCartFlow, setTrue: openAddToCart, setFalse: closeAddToCart } = useBooleanState(false);

  const toggleLineSelection = useCallback((soNumber: string, lineIndex: number) => {
    setSelectionState((prev) => {
      const prevSelection = prev[soNumber] ?? [];
      if (prevSelection.includes(lineIndex)) {
        return { ...prev, [soNumber]: prevSelection.filter((index) => index !== lineIndex) };
      }
      return { ...prev, [soNumber]: prevSelection.concat(lineIndex) };
    });
  }, []);

  const toggleOrderSelection = useCallback((soNumber: string, selectableIndexes: number[]) => {
    setSelectionState((prev) => {
      const current = prev[soNumber] ?? [];
      const isAllSelected = selectableIndexes.length > 0 && selectableIndexes.every((index) => current.includes(index));
      return { ...prev, [soNumber]: isAllSelected ? [] : selectableIndexes };
    });
  }, []);

  const flatSelectedLineItems = useMemo(() => {
    const selectedItems = [] as { soNumber: string; lineItem: OrderData["products"][number] }[];
    for (const row of sortedRows) {
      const soNumber = row.data.soNumber;
      const indexes = selectionState[soNumber] ?? [];
      indexes.forEach((index) => {
        const lineItem = row.data.products[index];
        if (lineItem?.product?.url && lineItem?.confirmedQty) {
          selectedItems.push({ soNumber, lineItem });
        }
      });
    }
    return selectedItems;
  }, [selectionState, sortedRows]);

  const selectedATCItems = useMemo(() => {
    if (flatSelectedLineItems.length === 0) {
      return selectedATCItemsDefaultValue;
    }
    const currencyIso = getOrderLineItemTotalPrice(flatSelectedLineItems[0].lineItem).currencyIso;

    const { totalPieces, totalPrice } = flatSelectedLineItems.reduce(
      (acc, item) => {
        acc.totalPieces += item.lineItem.confirmedQty;
        const priceData = getOrderLineItemTotalPrice(item.lineItem);
        if (priceData.currencyIso !== currencyIso) {
          throw new Error("Mismatched currencies in selected items");
        }
        acc.totalPrice += priceData.value;
        return acc;
      },
      { totalPieces: 0, totalPrice: 0 },
    );

    return {
      selectedLineItems: flatSelectedLineItems.length,
      selectedQuantity: totalPieces,
      totalPrice: {
        currencyIso,
        value: totalPrice,
      },
    } satisfies SelectedATCItemsSummary;
  }, [flatSelectedLineItems]);

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const handleOpenAddToCart = useCallback(() => {
    if (webservicesDisabled || flatSelectedLineItems.length === 0) {
      return;
    }
    openAddToCart();
  }, [flatSelectedLineItems.length, openAddToCart, webservicesDisabled]);

  const addToCartSelection = useMemo(() => flatSelectedLineItems.map((item) => item.lineItem), [flatSelectedLineItems]);

  const resetSelection = useCallback(() => {
    setSelectionState({});
  }, []);

  const rowVirtualizer = useWindowVirtualizer({
    count: sortedRows.length,
    getItemKey: (index) => sortedRows[index]?.data.soNumber ?? String(index),
    estimateSize: () => 156,
    overscan: 8,
    scrollPaddingStart: 200,
    scrollPaddingEnd: 200,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalVirtualHeight = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0 ? totalVirtualHeight - virtualRows[virtualRows.length - 1].end : 0;

  return (
    <Stack
      sx={{
        width: "100%",
        position: "relative",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Box sx={{ height: `${paddingTop}px` }} />
        {virtualRows.map((virtualRow) => {
          const row = sortedRows[virtualRow.index];

          if (!row) {
            return null;
          }

          return (
            <Box
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              sx={{
                width: "100%",
                pb: virtualRow.index === sortedRows.length - 1 ? 0 : 2,
              }}
            >
              <OrderCard
                row={row}
                onToggleOpen={toggleOpen}
                selection={selectionState[row.data.soNumber] ?? []}
                onSelectLine={(lineIndex: number) => {
                  toggleLineSelection(row.data.soNumber, lineIndex);
                }}
                onSelectAll={(selectableIndexes: number[], newValue: boolean) =>
                  toggleOrderSelection(row.data.soNumber, newValue ? selectableIndexes : [])
                }
              />
            </Box>
          );
        })}
        <Box sx={{ height: `${paddingBottom}px` }} />
      </Box>
      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={handleOpenAddToCart}
          loading={webservicesDisabled}
        />
      )}
      {showAddToCartFlow && (
        <OrderDetailsAddToCartFlow
          onClose={() => {
            closeAddToCart();
            resetSelection();
          }}
          selectedOrderItems={addToCartSelection}
        />
      )}
    </Stack>
  );
};
